import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Heading,
  Text,
  VStack,
  Progress,
  Flex,
  Card,
  CardBody,
  Spinner,
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Badge,
  SimpleGrid,
} from '@chakra-ui/react';
import axios from 'axios';
import { apiService, Question } from '../services/api';
import { LikertAnswerOptions } from '../components/LikertAnswerOptions';
import { resolveTestLanguage, testMessages } from '../i18n/testMessages';
import {
  StudentContext,
  TestProgressContext,
  isTestProgressContext,
  readSessionStorageRecord,
  validateReturnUrl,
  writeSessionStorageRecord,
} from '../utils/testFlowSecurity';

const TEST_PROGRESS_STORAGE_KEY = 'testInProgressAnswers';


const findQueryValue = (keys: string[], searchParams: URLSearchParams): string | undefined => {
  for (const key of keys) {
    const directValue = searchParams.get(key);

    if (directValue) {
      return directValue;
    }
  }

  const rawHref = window.location.href;

  for (const key of keys) {
    const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`${escapedKey}=([^&#]*)`, 'i');
    const match = rawHref.match(regex);

    if (match?.[1]) {
      try {
        return decodeURIComponent(match[1]);
      } catch {
        return match[1];
      }
    }
  }

  return undefined;
};

const extractNumericId = (value?: string): number | undefined => {
  if (!value) {
    return undefined;
  }

  const trimmedValue = value.trim();
  if (!/^\d+$/.test(trimmedValue)) {
    return undefined;
  }

  const parsedNumber = Number(trimmedValue);
  return Number.isInteger(parsedNumber) && parsedNumber > 0 ? parsedNumber : undefined;
};

const Test: React.FC = () => {
  const [searchParams] = useSearchParams();

  // Respuestas en curso: se restauran de forma sincrona (lazy initializer) en
  // vez de en un efecto, para no tener que lidiar con la carrera entre "cargar
  // lo guardado" y "guardar el estado inicial vacio" pisando lo restaurado.
  const restoredProgress = (): TestProgressContext | undefined => {
    const explicitStudentId = findQueryValue(
      ['studentId', 'studentID', 'student_id', 'idStudent'],
      searchParams
    );
    if (!explicitStudentId) {
      return undefined;
    }
    const stored = readSessionStorageRecord(
      TEST_PROGRESS_STORAGE_KEY,
      window.location.origin,
      isTestProgressContext
    );
    return stored && stored.studentId === explicitStudentId ? stored : undefined;
  };

  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentPage, setCurrentPage] = useState(() => restoredProgress()?.currentPage ?? 1);
  const [answers, setAnswers] = useState<Record<number, number>>(
    () => restoredProgress()?.answers ?? {}
  );
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contextError, setContextError] = useState<string | null>(null);

  const navigate = useNavigate();
  const toast = useToast();
  const [studentContext, setStudentContext] = useState<StudentContext>({});
  const messages = testMessages[resolveTestLanguage(searchParams)];
  const currentOrigin = window.location.origin;

  useEffect(() => {
    const explicitStudentId =
      findQueryValue(['studentId', 'studentID', 'student_id', 'idStudent'], searchParams) ||
      undefined;
    const explicitReturnUrl =
      findQueryValue(['returnUrl', 'return_url'], searchParams) ||
      undefined;
    const validatedReturnUrl = validateReturnUrl(explicitReturnUrl, currentOrigin);
    const nextStudentId = explicitStudentId || undefined;
    const parsedStudentId = extractNumericId(nextStudentId);

    const nextContext: StudentContext = {
      studentId: nextStudentId,
      studentName:
        findQueryValue(['studentName', 'student_name', 'name'], searchParams) ||
        undefined,
      studentEmail:
        findQueryValue(['studentEmail', 'student_email', 'email'], searchParams) ||
        undefined,
      centerId:
        findQueryValue(['centerId', 'center_id'], searchParams) ||
        undefined,
      directorId:
        findQueryValue(['directorId', 'director_id'], searchParams) ||
        undefined,
      returnUrl: validatedReturnUrl,
    };

    if (!parsedStudentId) {
      sessionStorage.removeItem('testStudentContext');
      sessionStorage.removeItem('testReturnContext');
      sessionStorage.removeItem('enneagramResults');
      sessionStorage.removeItem(TEST_PROGRESS_STORAGE_KEY);
      setContextError(messages.invalidStudentId);
    } else {
      setContextError(null);
    }

    if (explicitReturnUrl && !validatedReturnUrl) {
      setContextError(messages.invalidReturnUrl);
    }

    setStudentContext(nextContext);
  }, [searchParams, currentOrigin, messages.invalidStudentId, messages.invalidReturnUrl]);

  useEffect(() => {
    const parsedStudentId = extractNumericId(studentContext.studentId);

    if (!parsedStudentId) {
      return;
    }

    writeSessionStorageRecord('testStudentContext', studentContext, currentOrigin);
  }, [studentContext, currentOrigin]);

  // Persiste las respuestas en curso (AID-131): un refresco, un cierre de
  // pestaña o una caida de red ya no borran el progreso. El estado inicial de
  // answers/currentPage ya viene restaurado (lazy initializer, arriba), asi
  // que este efecto solo necesita mantener sessionStorage al dia mientras el
  // alumno sigue respondiendo.
  useEffect(() => {
    const parsedStudentId = extractNumericId(studentContext.studentId);

    if (!parsedStudentId) {
      return;
    }

    if (Object.keys(answers).length === 0) {
      sessionStorage.removeItem(TEST_PROGRESS_STORAGE_KEY);
      return;
    }

    writeSessionStorageRecord<TestProgressContext>(
      TEST_PROGRESS_STORAGE_KEY,
      { studentId: studentContext.studentId, currentPage, answers },
      currentOrigin
    );
  }, [answers, currentPage, studentContext.studentId, currentOrigin]);

  const questionsPerPage = 10;
  const totalPages = Math.ceil(questions.length / questionsPerPage);
  const progress = questions.length > 0
    ? (Object.keys(answers).length / questions.length) * 100
    : 0;

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        setLoading(true);
        const fetchedQuestions = await apiService.getQuestions();
        setQuestions(fetchedQuestions);
        setError(null);
      } catch (err) {
        console.error('Error fetching questions:', err);
        setError(messages.loadErrorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, [messages.loadErrorMessage]);

  const getCurrentPageQuestions = useCallback(() => {
    const startIndex = (currentPage - 1) * questionsPerPage;
    return questions.slice(startIndex, startIndex + questionsPerPage);
  }, [questions, currentPage]);

  const handleAnswerChange = (questionId: number, value: number) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo(0, 0);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async () => {
    if (Object.keys(answers).length !== questions.length) {
      toast({
        title: messages.incompleteTitle,
        description: messages.incompleteDescription,
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      setSubmitting(true);

      const answersArray = questions.map(q => answers[q.id]);
      const resolvedStudentId = extractNumericId(
        studentContext.studentId ||
        findQueryValue(['studentId', 'studentID', 'student_id', 'idStudent'], searchParams)
      );
      const resolvedCenterId = extractNumericId(
        studentContext.centerId ||
        findQueryValue(['centerId', 'center_id'], searchParams)
      );
      const resolvedDirectorId = extractNumericId(
        studentContext.directorId ||
        findQueryValue(['directorId', 'director_id'], searchParams)
      );
      const resolvedStudentName =
        studentContext.studentName ||
        findQueryValue(['studentName', 'student_name', 'name'], searchParams);
      const resolvedStudentEmail =
        studentContext.studentEmail ||
        findQueryValue(['studentEmail', 'student_email', 'email'], searchParams);
      const resolvedReturnUrl = validateReturnUrl(
        studentContext.returnUrl ||
        findQueryValue(['returnUrl', 'return_url'], searchParams),
        currentOrigin
      );

      if (resolvedStudentId === undefined) {
        throw new Error(messages.missingStudentOnSubmit);
      }

      const submitStudentData: {
        studentId: number;
        studentName?: string;
        studentEmail?: string;
        centerId?: number;
        directorId?: number;
      } = {
        studentId: resolvedStudentId,
      };

      if (resolvedStudentName) {
        submitStudentData.studentName = resolvedStudentName;
      }

      if (resolvedStudentEmail) {
        submitStudentData.studentEmail = resolvedStudentEmail;
      }

      if (resolvedCenterId !== undefined) {
        submitStudentData.centerId = resolvedCenterId;
      }

      if (resolvedDirectorId !== undefined) {
        submitStudentData.directorId = resolvedDirectorId;
      }

      const result = await apiService.submitAnswers(answersArray, submitStudentData);

      sessionStorage.setItem('enneagramResults', JSON.stringify(result));
      sessionStorage.removeItem(TEST_PROGRESS_STORAGE_KEY);
      writeSessionStorageRecord('testReturnContext', {
        studentId: String(resolvedStudentId),
        returnUrl: resolvedReturnUrl,
        mainType: String(result.enneagram_type),
        wingType: String(result.wing),
        sessionId: result.session_id || '',
      }, currentOrigin);

      navigate('/results');
    } catch (err) {
      console.error('Error submitting answers:', err);

      const backendMessage = axios.isAxiosError(err)
        ? err.response?.data?.detail || err.response?.data?.message
        : err instanceof Error
          ? err.message
          : undefined;

      toast({
        title: messages.submitErrorTitle,
        description: backendMessage || messages.submitErrorDescription,
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box textAlign="center" py={10}>
        <Spinner size="xl" color="primary.500" />
        <Text mt={4}>{messages.loadingQuestions}</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        status="error"
        variant="subtle"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        textAlign="center"
        height="200px"
        borderRadius="md"
        my={8}
      >
        <AlertIcon boxSize="40px" mr={0} />
        <AlertTitle mt={4} mb={1} fontSize="lg">
          {messages.loadErrorTitle}
        </AlertTitle>
        <AlertDescription maxWidth="sm">
          {error}
        </AlertDescription>
      </Alert>
    );
  }

  if (contextError) {
    return (
      <Alert
        status="warning"
        variant="subtle"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        textAlign="center"
        minHeight="220px"
        borderRadius="md"
        my={8}
      >
        <AlertIcon boxSize="40px" mr={0} />
        <AlertTitle mt={4} mb={2} fontSize="lg">
          {messages.cannotStart}
        </AlertTitle>
        <AlertDescription maxWidth="md">
          {contextError}
        </AlertDescription>
        <Button mt={6} colorScheme="blue" onClick={() => navigate('/')}>
          {messages.backHome}
        </Button>
      </Alert>
    );
  }

  const currentQuestions = getCurrentPageQuestions();

  return (
    <Box>
      <Heading mb={4} color="primary.600">{messages.heading}</Heading>
      <Text mb={6}>{messages.intro}</Text>

      <Card mb={6} borderColor="blue.100" bg="blue.50">
        <CardBody>
          <Flex direction={{ base: 'column', md: 'row' }} justify="space-between" gap={4}>
            <Box>
              <Badge colorScheme="blue" mb={2}>{messages.studentSessionBadge}</Badge>
              <Heading as="h2" size="sm" mb={2} color="blue.800">
                {messages.studentSession(studentContext.studentName || messages.anonymousStudent)}
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                {studentContext.centerId && (
                  <Text fontSize="sm" color="blue.900">{messages.center(studentContext.centerId)}</Text>
                )}
                {studentContext.studentEmail && (
                  <Text fontSize="sm" color="blue.900">{messages.account(studentContext.studentEmail)}</Text>
                )}
              </SimpleGrid>
            </Box>
            <Box maxW="xl">
              <Text fontWeight="semibold" color="blue.900" mb={1}>
                {messages.privacyTitle}
              </Text>
              <Text fontSize="sm" color="blue.900">
                {messages.privacyText}
              </Text>
            </Box>
          </Flex>
        </CardBody>
      </Card>

      <Box mb={8}>
        <Flex justify="space-between" mb={2}>
          <Text fontSize="sm">{messages.answeredCount(Object.keys(answers).length, questions.length)}</Text>
          <Text fontSize="sm">{Math.round(progress)}%</Text>
        </Flex>
        <Progress value={progress} colorScheme="blue" borderRadius="md" />
      </Box>

      <VStack spacing={6} align="stretch" mb={8}>
        {currentQuestions.map(question => (
          <Card key={question.id} variant="outline">
            <CardBody>
              <Box mb={4}>
                <Text fontWeight="medium">
                  {question.id}. {question.text}
                </Text>
              </Box>

              <LikertAnswerOptions
                questionId={question.id}
                questionText={question.text}
                value={answers[question.id]}
                onChange={value => handleAnswerChange(question.id, value)}
                labels={messages.likert}
                selectedLabel={messages.selected}
              />
            </CardBody>
          </Card>
        ))}
      </VStack>

      <Flex justify="space-between" align="center">
        <Button
          onClick={goToPrevPage}
          isDisabled={currentPage === 1}
          colorScheme="gray"
        >
          {messages.previous}
        </Button>

        <Text>
          {messages.pageIndicator(currentPage, totalPages)}
        </Text>

        {currentPage < totalPages ? (
          <Button
            onClick={goToNextPage}
            colorScheme="blue"
            isDisabled={currentQuestions.some(q => !answers[q.id])}
          >
            {messages.next}
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            colorScheme="green"
            isLoading={submitting}
            loadingText={messages.submitting}
            isDisabled={currentQuestions.some(q => !answers[q.id])}
          >
            {messages.finish}
          </Button>
        )}
      </Flex>
    </Box>
  );
};

export default Test;
